/**
 * Flashcard Generator Component
 * Modal/form to configure flashcard generation
 */

import {useState} from 'react';
import {Button} from '../ui/Button';
import {cn} from '../../utils/cn';

interface FlashcardGeneratorProps {
  documentTitle: string;
  availableTopics?: string[];
  onGenerate: (options: {
    count: number;
    topics?: string[];
    difficulty: 'easy' | 'medium' | 'hard';
  }) => void;
  onCancel: () => void;
  isGenerating?: boolean;
}

export function FlashcardGenerator({
  documentTitle,
  availableTopics = [],
  onGenerate,
  onCancel,
  isGenerating = false,
}: FlashcardGeneratorProps) {
  const [count, setCount] = useState(20);
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium');
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);

  const handleTopicToggle = (topic: string) => {
    setSelectedTopics((prev) =>
      prev.includes(topic) ? prev.filter((t) => t !== topic) : [...prev, topic]
    );
  };

  const handleGenerate = () => {
    onGenerate({
      count,
      topics: selectedTopics.length > 0 ? selectedTopics : undefined,
      difficulty,
    });
  };

  return (
    <div className="bg-white dark:bg-[#1E1E1E] rounded-lg border border-gray-200 dark:border-gray-700 p-6 max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-2">
        Generate Flashcards
      </h2>
      <p className="text-textSecondary dark:text-[#858585] mb-6">
        Create flashcards from: <span className="font-medium">{documentTitle}</span>
      </p>

      {/* Number of Cards */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-textPrimary dark:text-[#CCCCCC] mb-2">
          Number of Cards
        </label>
        <input
          type="number"
          min="1"
          max="100"
          value={count}
          onChange={(e) => setCount(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-[#252526] text-textPrimary dark:text-[#CCCCCC] focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
        <p className="mt-1 text-xs text-textTertiary dark:text-[#6A6A6A]">
          Between 1 and 100 cards
        </p>
      </div>

      {/* Difficulty */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-textPrimary dark:text-[#CCCCCC] mb-2">
          Difficulty
        </label>
        <div className="flex gap-2">
          {(['easy', 'medium', 'hard'] as const).map((level) => (
            <button
              key={level}
              onClick={() => setDifficulty(level)}
              className={cn(
                'flex-1 px-4 py-2 rounded-lg font-medium transition-colors capitalize',
                difficulty === level
                  ? 'bg-orange-600 dark:bg-orange-500 text-white'
                  : 'bg-backgroundSecondary dark:bg-[#252526] text-textSecondary dark:text-[#858585] hover:bg-gray-200 dark:hover:bg-[#3E3E42]'
              )}
            >
              {level}
            </button>
          ))}
        </div>
      </div>

      {/* Topics (if available) */}
      {availableTopics.length > 0 && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-textPrimary dark:text-[#CCCCCC] mb-2">
            Topics (Optional - leave empty for all topics)
          </label>
          <div className="flex flex-wrap gap-2">
            {availableTopics.map((topic) => (
              <button
                key={topic}
                onClick={() => handleTopicToggle(topic)}
                className={cn(
                  'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                  selectedTopics.includes(topic)
                    ? 'bg-orange-600 dark:bg-orange-500 text-white'
                    : 'bg-backgroundSecondary dark:bg-[#252526] text-textSecondary dark:text-[#858585] hover:bg-gray-200 dark:hover:bg-[#3E3E42]'
                )}
              >
                {topic}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-3">
        <Button onClick={onCancel} variant="outline" disabled={isGenerating}>
          Cancel
        </Button>
        <Button onClick={handleGenerate} variant="primary" disabled={isGenerating}>
          {isGenerating ? 'Generating...' : 'Generate Flashcards'}
        </Button>
      </div>
    </div>
  );
}

